import React from "react"; // Removed * as React, typically React is imported directly.

import { getInitialState, getIfSlideIsVisbile } from "./utils/index";

/**
 * @typedef {object} CarouselInternalState
 * @property {number} itemWidth
 * @property {number} currentSlide
 * @property {number} transform
 * @property {number} totalItems
 * @property {number} slidesToShow
 * @property {number} containerWidth
 * @property {number} domFullyLoaded
 */

/**
 * @typedef {object} CarouselProps
 * @property {boolean} infinite
 * @property {string} itemClass
 * @property {string} [itemAriaLabel]
 * @property {boolean} [partialVisbile] // Old typo, kept for compatibility
 * @property {boolean} [partialVisible]
 * @property {boolean} [focusOnSelect]
 * @property {React.ReactNode} children
 */

/**
 * @typedef {object} SkipCallbackOptions
 * @property {boolean} [disableCallback]
 */

/**
 * @typedef {object} CarouselItemsProps
 * @property {CarouselProps} props
 * @property {CarouselInternalState} state
 * @property {any[]} clones
 * @property {boolean} notEnoughChildren
 * @property {(index: number, skipCallbacks?: SkipCallbackOptions) => void} goToSlide
 */

/**
 * Renders the carousel items, handling infinite mode, visibility, and styling.
 * @param {CarouselItemsProps} { props, state, goToSlide, clones, notEnoughChildren }
 * @returns {JSX.Element | null}
 */
const CarouselItems = ({
  props,
  state,
  goToSlide,
  clones,
  notEnoughChildren,
}) => {
  const { itemWidth } = state;
  const {
    children,
    infinite,
    itemClass,
    itemAriaLabel,
    partialVisbile, // Old typo, kept for compatibility
    partialVisible,
  } = props;

  const {
    flexBisis,
    shouldRenderOnSSR,
    domFullyLoaded,
    partialVisibilityGutter,
    shouldRenderAtAll,
  } = getInitialState(state, props);

  // If shouldRenderAtAll is false, do not render anything.
  if (!shouldRenderAtAll) {
    return null;
  }

  // Warn about the old typo for partialVisible prop.
  if (partialVisbile) {
    console.warn(
      'WARNING: Please correct props name: "partialVisible" as old typo will be removed in future versions!'
    );
  }

  return (
    <>
      {/* Map through children or clones based on the infinite prop. */}
      {(infinite ? clones : React.Children.toArray(children)).map(
        (child, index) => {
          return (
            <li
              key={index}
              data-index={index}
              onClick={() => {
                // If focusOnSelect is enabled, navigate to the clicked slide.
                if (props.focusOnSelect) {
                  goToSlide(index);
                }
              }}
              // Set aria-hidden based on whether the slide is currently visible.
              aria-hidden={getIfSlideIsVisbile(index, state) ? "false" : "true"}
              aria-label={
                // Prioritize itemAriaLabel, then child's ariaLabel, otherwise null.
                itemAriaLabel
                  ? itemAriaLabel
                  : child.props.ariaLabel
                  ? child.props.ariaLabel
                  : null
              }
              style={{
                // Set flex basis for SSR, otherwise auto.
                flex: shouldRenderOnSSR ? `1 0 ${flexBisis}%` : "auto",
                position: "relative",
                // Set width based on domFullyLoaded and partial visibility.
                width: domFullyLoaded
                  ? `${
                      // Apply partialVisibilityGutter if partial visible and not enough children.
                      (partialVisbile || partialVisible) &&
                      partialVisibilityGutter &&
                      !notEnoughChildren
                        ? itemWidth - partialVisibilityGutter
                        : itemWidth
                    }px`
                  : "auto",
              }}
              // Apply dynamic classes for styling based on slide visibility.
              className={`react-multi-carousel-item ${
                getIfSlideIsVisbile(index, state)
                  ? "react-multi-carousel-item--active"
                  : ""
              } ${itemClass}`}
            >
              {child}
            </li>
          );
        }
      )}
    </>
  );
};

export default CarouselItems;
